import { useCallback, useEffect, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import type { CallerProvidedTagsT, FrameDecodeTagsT, IncrementCountInputT, PerformanceMetricActionT } from '../../image/labelling-v2/analytics/types/performance';
import { PERFORMANCE_METRIC_EVENTS } from '../../image/labelling-v2/constants/performance';
import { extractFrameProcessorErrorCategory } from '../../image/labelling-v2/utils/frame-processor-error-utils';
import { FRAME_PROCESSOR_INPUT_MESSAGES, FRAME_PROCESSOR_OUTPUT_MESSAGES } from '../../workers/constants/frame-processor-constants';
import type { WebWorker } from '../../workers/types/common';
import type { VideoFrameProcessorWorkerOutput } from '../../workers/types/video-frame-processor';
type FrameDecoderPropsT = {
  videoFrameProcessor?: WebWorker;
  /** Optional performance metrics callback for async timing. */
  measureLatency?: (name: PerformanceMetricActionT) => (tags?: CallerProvidedTagsT | FrameDecodeTagsT) => void;
  /** Optional performance metrics callback for error counting. */
  incrementCount?: (input: IncrementCountInputT) => void;
};
type FrameDecoderResultT = {
  decodeFrame: (frameIndex: number) => Promise<ImageBitmap>;
};
export function useFrameDecoder({
  videoFrameProcessor,
  measureLatency,
  incrementCount
}: FrameDecoderPropsT): FrameDecoderResultT {
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const pendingDecodeFrameRef = useRef<{
    resolve: (imageBitmap: ImageBitmap) => void;
    reject: (error: Error) => void;
  } | null>(null);
  const startMeasureLatencyRef = useRef(measureLatency);
  // eslint-disable-next-line react-hooks/refs
  startMeasureLatencyRef.current = measureLatency;
  const commitMeasureLatencyRef = useRef<((tags?: any) => void) | null>(null);
  const incrementCountRef = useRef(incrementCount);
  // eslint-disable-next-line react-hooks/refs
  incrementCountRef.current = incrementCount;
  const cleanup = useCallback((rejectReason?: string) => {
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
    if (rejectReason) {
      pendingDecodeFrameRef.current?.reject(new Error(rejectReason));
    }
    pendingDecodeFrameRef.current = null;
  }, []);
  const finish = useCallback((result: {
    imageBitmap?: ImageBitmap;
    error?: Error;
  }) => {
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
    if (result.error) {
      pendingDecodeFrameRef.current?.reject(result.error);
    } else if (result.imageBitmap) {
      pendingDecodeFrameRef.current?.resolve(result.imageBitmap);
    }
    pendingDecodeFrameRef.current = null;
  }, []);
  const decodeFrame = useCallback((frameIndex: number): Promise<ImageBitmap> => {
    if (!videoFrameProcessor) {
      return Promise.reject(new Error('Video frame processor not available'));
    }
    if (!videoFrameProcessor.isReady()) {
      const workerError = videoFrameProcessor.getError();
      return Promise.reject(new Error(workerError?.message ?? 'Video frame processor is not ready'));
    }
    cleanup('Decode superseded by new request');
    const requestId = uuid();
    return new Promise<ImageBitmap>((resolve, reject) => {
      pendingDecodeFrameRef.current = {
        resolve,
        reject
      };
      const handleWorkerMessage = (message: VideoFrameProcessorWorkerOutput) => {
        if (message.payload.requestId !== requestId) {
          return;
        }
        switch (message.type) {
          case FRAME_PROCESSOR_OUTPUT_MESSAGES.DECODED_FRAME:
            commitMeasureLatencyRef.current?.({
              chainLength: message.payload.chainLength,
              frameIndex: message.payload.frameIndex
            });
            commitMeasureLatencyRef.current = null;
            finish({
              imageBitmap: message.payload.imageBitmap
            });
            break;
          case FRAME_PROCESSOR_OUTPUT_MESSAGES.ERROR:
            if (incrementCountRef.current) {
              incrementCountRef.current({
                name: PERFORMANCE_METRIC_EVENTS.FRAME_DECODE_ERROR,
                tags: {
                  errorCategory: extractFrameProcessorErrorCategory(message.payload.message),
                  errorMessage: message.payload.message
                }
              });
            }
            finish({
              error: new Error(message.payload.message)
            });
            break;
          default:
            break;
        }
      };
      unsubscribeRef.current = videoFrameProcessor.subscribe(handleWorkerMessage);
      if (startMeasureLatencyRef.current) {
        commitMeasureLatencyRef.current = startMeasureLatencyRef.current(PERFORMANCE_METRIC_EVENTS.FRAME_DECODE_LATENCY);
      }
      videoFrameProcessor.postMessage({
        type: FRAME_PROCESSOR_INPUT_MESSAGES.DECODE_FRAME,
        payload: {
          frameIndex,
          requestId
        }
      });
    });
  }, [videoFrameProcessor, cleanup, finish]);
  useEffect(() => {
    return () => cleanup('Component unmounted before decode completed');
  }, [cleanup]);
  return {
    decodeFrame
  };
}